import type { Zone } from '@seek/audience-zones';

import { useCandidateId } from 'src/components/Profile/data';
import { usePage } from 'src/components/Router/usePage';
import type { Maybe, Page } from 'src/types';

import { useAppConfig } from '../appConfig';

import { isFeatureActive } from '.';

export type ExperimentVariant = '0' | '1' | '2';
// This value is staying in place as this code is currently being used to show
// a modal to 20% of candidates
const JOBSDB_HK_AB_TEST_ROLLOUT_PERCENTAGE = 20;

const abTestRolloutPercentageComparator = (percentage: number) =>
  percentage * 10;

const whiteList = [
  '26258543', // Simon Lusted
  '401092497', // rgillespie+4@seek.com.au (this account is being used to demo shareable profiles to internal stakeholders)
];

const inWhiteList = (candidateId: string) => whiteList.includes(candidateId);

// When the last 3 digits of candidate ID fall below the percentage*10 (ie, < 200 if 20%)
const inTestGroup = (
  candidateId: string,
  rolloutPercentage: number,
): boolean => {
  const inRandomGroup =
    parseInt(candidateId, 10) % 1000 <
    abTestRolloutPercentageComparator(rolloutPercentage);

  return inRandomGroup || inWhiteList(candidateId);
};

// When the last 3 digits of candidate ID fall between the percentage*10 ~ percentage*10*2 (ie, 201 - 399 if 20%)
const inControlGroup = (
  candidateId: string,
  rolloutPercentage: number,
): boolean => {
  const grouping = parseInt(candidateId, 10) % 1000;
  const inRandomGroup =
    grouping > abTestRolloutPercentageComparator(rolloutPercentage) &&
    grouping < abTestRolloutPercentageComparator(rolloutPercentage) * 2;

  return inRandomGroup || inWhiteList(candidateId);
};

const wasInANZTestingGroup = (
  zone: Zone,
  page: Page,
  candidateId: Maybe<string>,
): boolean => {
  const isAnz = zone === 'anz-1' || zone === 'anz-2';
  const isProfileAndANZ = isAnz && page === 'profile-page';

  return Boolean(
    isProfileAndANZ && candidateId && inTestGroup(candidateId, 20),
  );
};

export const getShareableProfileExperimentVariant = (
  zone: Zone,
  page: Page,
  candidateId: Maybe<string>,
): ExperimentVariant | undefined => {
  const isJobsDBHK = zone === 'asia-1';
  const isProfileAndJobsDBHK = isJobsDBHK && page === 'profile-page';

  const isShareableProfileFeatureFlagActive =
    isFeatureActive('shareable-profile');

  if (isProfileAndJobsDBHK && candidateId) {
    if (
      inTestGroup(candidateId, JOBSDB_HK_AB_TEST_ROLLOUT_PERCENTAGE) ||
      isShareableProfileFeatureFlagActive
    ) {
      return '1';
    }

    if (inControlGroup(candidateId, JOBSDB_HK_AB_TEST_ROLLOUT_PERCENTAGE)) {
      return '0';
    }

    return '2';
  }
};

export const useIsShareableProfileFeatureActive = () => {
  const page = usePage();
  const { zone, isUnifiedSite } = useAppConfig();
  const { candidateId } = useCandidateId();

  const wasInANZShareableProfileExperimentGroup = wasInANZTestingGroup(
    zone,
    page,
    candidateId,
  );

  const isShareableProfileExperimentActive =
    getShareableProfileExperimentVariant(zone, page, candidateId) === '1';

  const isShareableProfileFeatureFlagActive =
    isFeatureActive('shareable-profile');

  const isShareableProfileFeatureActive =
    isShareableProfileExperimentActive ||
    isShareableProfileFeatureFlagActive ||
    isUnifiedSite;

  return {
    wasInANZShareableProfileExperimentGroup,
    isShareableProfileFeatureActive,
  };
};
